<template>
  <div class="subsidiary-management">
    <header class="dashboard-header">
      <div class="header-content">
        <h1>Subsidiary Management</h1>
        <p class="subtitle">Manage the subsidiary settings for report submissions</p>
      </div>
    </header>

    <div class="registration-container">
      <div class="card">
        <div class="card-header">
          <h2>Reporting Basis Settings</h2>
        </div>
        
        <div class="card-content">
          <div v-if="isMobile">
            <div v-for="subsidiary in subsidiaries" :key="subsidiary.id" class="subsidiary-mobile-card">
              <div class="mobile-row">
                <span class="mobile-label">Subsidiary Name:</span>
                <span class="mobile-value">{{ subsidiary.name }}</span>
              </div>
              <div class="mobile-row">
                <span class="mobile-label">Reporting Basis:</span>
                <el-select 
                  v-model="subsidiary.reporting_basis" 
                  @change="updateReportingBasis(subsidiary)"
                  :disabled="loading"
                  class="basis-select"
                  size="small"
                >
                  <el-option label="Daily" value="daily" />
                  <el-option label="Weekly" value="weekly" />
                </el-select>
              </div>
            </div>
          </div>
          <el-table 
            v-else
            :data="subsidiaries" 
            style="width: 100%" 
            v-loading="loading"
            :header-cell-style="{
              background: '#f1f5f9',
              color: '#1e293b',
              fontWeight: '600',
              fontSize: '0.875rem',
              padding: '16px 20px',
              borderBottom: '2px solid #e2e8f0'
            }"
            :cell-style="{
              padding: '16px 20px',
              fontSize: '0.875rem',
              color: '#1e293b'
            }"
          >
            <el-table-column prop="name" label="Subsidiary Name" min-width="300">
              <template #default="scope">
                <div class="subsidiary-name">
                  <span class="name">{{ scope.row.name }}</span>
                </div>
              </template>
            </el-table-column>
            
            <el-table-column prop="reporting_basis" label="Reporting Basis" width="250">
              <template #default="scope">
                <div class="basis-cell">
                  <el-select 
                    v-model="scope.row.reporting_basis" 
                    @change="updateReportingBasis(scope.row)"
                    :disabled="loading"
                    class="basis-select"
                  >
                    <el-option label="Daily" value="daily" />
                    <el-option label="Weekly" value="weekly" />
                  </el-select>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>

    <!-- New Expense Types Card -->
    <div class="registration-container mt-8">
      <div class="card">
        <div class="card-header">
          <h2>Cost & Expense Types</h2>
          <div class="header-actions">
            <el-button 
              type="primary" 
              @click="openAddTypeDialog"
              :disabled="loadingExpenseTypes"
            >
              Add New Type
            </el-button>
          </div>
        </div>
        
        <div class="card-content">
          <div v-if="isMobile">
            <div v-for="type in expenseTypes" :key="type.id" class="expense-mobile-card">
              <div class="mobile-row">
                <span class="mobile-label">Type:</span>
                <span class="mobile-value">{{ type.type }}</span>
              </div>
              <div class="mobile-row">
                <span class="mobile-label">Category:</span>
                <el-tag :type="type.category === 'stock' ? 'primary' : type.category === 'cost' ? 'success' : 'warning'">
                  {{ type.category }}
                </el-tag>
              </div>
              <div class="mobile-row action-row">
                <el-button 
                  type="primary" 
                  size="small" 
                  @click="editType(type)"
                  :disabled="loadingExpenseTypes"
                >
                  Edit
                </el-button>
                <el-button 
                  type="danger" 
                  size="small" 
                  @click="confirmDelete(type)"
                  :disabled="loadingExpenseTypes"
                >
                  Delete
                </el-button>
              </div>
            </div>
          </div>
          <el-table 
            v-else
            :data="expenseTypes" 
            style="width: 100%" 
            v-loading="loadingExpenseTypes"
            :header-cell-style="{
              background: '#f1f5f9',
              color: '#1e293b',
              fontWeight: '600',
              fontSize: '0.875rem',
              padding: '16px 20px',
              borderBottom: '2px solid #e2e8f0'
            }"
            :cell-style="{
              padding: '16px 20px',
              fontSize: '0.875rem',
              color: '#1e293b'
            }"
          >
            <el-table-column prop="type" label="Type" min-width="300">
              <template #default="scope">
                <div class="type-name">
                  <span class="name">{{ scope.row.type }}</span>
                </div>
              </template>
            </el-table-column>
            
            <el-table-column prop="category" label="Category" width="200">
              <template #default="scope">
                <el-tag :type="scope.row.category === 'stock' ? 'primary' : scope.row.category === 'cost' ? 'success' : 'warning'">
                  {{ scope.row.category }}
                </el-tag>
              </template>
            </el-table-column>

            <el-table-column label="Actions" width="200" align="center">
              <template #default="scope">
                <div class="action-buttons">
                  <el-button 
                    type="primary" 
                    size="small" 
                    @click="editType(scope.row)"
                    :disabled="loadingExpenseTypes"
                  >
                    Edit
                  </el-button>
                  <el-button 
                    type="danger" 
                    size="small" 
                    @click="confirmDelete(scope.row)"
                    :disabled="loadingExpenseTypes"
                  >
                    Delete
                  </el-button>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>

      <!-- Add/Edit Type Dialog -->
      <el-dialog
        :title="editingType ? 'Edit Type' : 'Add New Type'"
        v-model="showAddTypeDialog"
        width="500px"
      >
        <el-form :model="newType" label-width="100px">
          <el-form-item label="Subsidiary" v-if="!editingType">
            <el-select 
              v-model="newType.subsidiary_id" 
              placeholder="Select subsidiary"
              :disabled="loadingExpenseTypes"
              style="width: 100%"
              @change="handleSubsidiarySelect"
            >
              <el-option 
                v-for="sub in subsidiaries" 
                :key="sub.id" 
                :label="sub.name" 
                :value="sub.id" 
              />
            </el-select>
          </el-form-item>

          <template v-if="!editingType">
            <el-form-item label="Selection">
              <el-radio-group v-model="typeSelection" class="type-selection-group">
                <el-radio label="existing">Use Existing Type</el-radio>
                <el-radio label="new">Create New Type</el-radio>
              </el-radio-group>
            </el-form-item>

            <template v-if="typeSelection === 'existing'">
              <el-form-item label="Type">
                <el-select 
                  v-model="newType.selectedTypes" 
                  placeholder="Select existing types"
                  :disabled="loadingExpenseTypes || !newType.subsidiary_id"
                  style="width: 100%"
                  filterable
                  multiple
                  collapse-tags
                  collapse-tags-tooltip
                  @change="handleTypeSelect"
                >
                  <el-option
                    v-for="type in availableTypes"
                    :key="type.id"
                    :label="type.type"
                    :value="type.id"
                  >
                    <span>{{ type.type }}</span>
                    <span style="float: right; color: #8492a6; font-size: 13px">
                      {{ type.category }}
                    </span>
                  </el-option>
                  <template v-if="availableTypes.length === 0">
                    <el-option disabled>
                      <span style="color: #909399">No available types</span>
                    </el-option>
                  </template>
                </el-select>
              </el-form-item>
            </template>

            <template v-if="typeSelection === 'new'">
              <el-form-item label="Type Name">
                <el-input 
                  v-model="newType.type" 
                  placeholder="Enter type name"
                  :disabled="loadingExpenseTypes"
                />
              </el-form-item>
              <el-form-item label="Category">
                <el-select 
                  v-model="newType.category" 
                  placeholder="Select category"
                  :disabled="loadingExpenseTypes"
                  style="width: 100%"
                >
                  <el-option label="Stock" value="stock" />
                  <el-option label="Cost" value="cost" />
                  <el-option label="Expense" value="expense" />
                </el-select>
              </el-form-item>
            </template>
          </template>

          <template v-else>
            <el-form-item label="Type Name">
              <el-input 
                v-model="newType.type" 
                placeholder="Enter type name"
                :disabled="loadingExpenseTypes"
              />
            </el-form-item>
            <el-form-item label="Category">
              <el-select 
                v-model="newType.category" 
                placeholder="Select category"
                :disabled="loadingExpenseTypes"
                style="width: 100%"
              >
                <el-option label="Stock" value="stock" />
                <el-option label="Cost" value="cost" />
                <el-option label="Expense" value="expense" />
              </el-select>
            </el-form-item>
          </template>
        </el-form>
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="showAddTypeDialog = false">Cancel</el-button>
            <el-button 
              type="primary" 
              @click="saveType"
              :disabled="!isFormValid || loadingExpenseTypes"
            >
              {{ getSubmitButtonText }}
            </el-button>
          </span>
        </template>
      </el-dialog>

      <!-- Delete Confirmation Dialog -->
      <el-dialog
        title="Confirm Delete"
        v-model="showDeleteDialog"
        width="400px"
      >
        <p>Are you sure you want to delete this type?</p>
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="showDeleteDialog = false">Cancel</el-button>
            <el-button 
              type="danger" 
              @click="deleteType"
              :disabled="loadingExpenseTypes"
            >
              Delete
            </el-button>
          </span>
        </template>
      </el-dialog>
    </div>

    <!-- Add Confirmation Dialog for Reporting Basis Change -->
    <el-dialog
      title="Change Reporting Basis"
      v-model="showBasisChangeDialog"
      width="500px"
    >
      <div class="basis-change-warning">
        <i class="fa-solid fa-triangle-exclamation"></i>
        <p>Changing the reporting basis will require re-entering all previous data in the new format.</p>
        <p>Are you sure you want to change the reporting basis from <strong>{{ oldBasis }}</strong> to <strong>{{ newBasis }}</strong>?</p>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showBasisChangeDialog = false">Cancel</el-button>
          <el-button 
            type="primary" 
            @click="confirmBasisChange"
            :disabled="loading"
          >
            Confirm Change
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- Branch Management Card -->
    <div class="registration-container mt-8">
      <div class="card">
        <div class="card-header">
          <h2>Branch Management</h2>
          <div class="header-actions">
            <el-button 
              type="primary" 
              @click="showAddBranchDialog = true"
              :disabled="loadingBranches"
            >
              Add New Branch
            </el-button>
          </div>
        </div>
        
        <div class="card-content">
          <div v-if="isMobile">
            <div v-for="branch in branches" :key="branch.id" class="branch-mobile-card">
              <div class="mobile-row">
                <span class="mobile-label">Branch Name:</span>
                <span class="mobile-value">{{ branch.branch_name }}</span>
              </div>
              <div class="mobile-row">
                <span class="mobile-label">Subsidiary:</span>
                <span class="mobile-value">{{ getSubsidiaryName(branch.subsidiary_id) }}</span>
              </div>
              <div class="mobile-row action-row">
                <el-button 
                  type="primary" 
                  size="small" 
                  @click="editBranch(branch)"
                  :disabled="loadingBranches"
                >
                  Edit
                </el-button>
                <el-button 
                  type="danger" 
                  size="small" 
                  @click="confirmDeleteBranch(branch)"
                  :disabled="loadingBranches"
                >
                  Delete
                </el-button>
              </div>
            </div>
          </div>
          <el-table 
            v-else
            :data="branches" 
            style="width: 100%" 
            v-loading="loadingBranches"
            :header-cell-style="{
              background: '#f1f5f9',
              color: '#1e293b',
              fontWeight: '600',
              fontSize: '0.875rem',
              padding: '16px 20px',
              borderBottom: '2px solid #e2e8f0'
            }"
            :cell-style="{
              padding: '16px 20px',
              fontSize: '0.875rem',
              color: '#1e293b'
            }"
          >
            <el-table-column prop="branch_name" label="Branch Name" min-width="300">
              <template #default="scope">
                <div class="branch-name">
                  <span class="name">{{ scope.row.branch_name }}</span>
                </div>
              </template>
            </el-table-column>
            
            <el-table-column prop="subsidiary_id" label="Subsidiary" min-width="200">
              <template #default="scope">
                <span>{{ getSubsidiaryName(scope.row.subsidiary_id) }}</span>
              </template>
            </el-table-column>

            <el-table-column label="Actions" width="200" align="center">
              <template #default="scope">
                <div class="action-buttons">
                  <el-button 
                    type="primary" 
                    size="small" 
                    @click="editBranch(scope.row)"
                    :disabled="loadingBranches"
                  >
                    Edit
                  </el-button>
                  <el-button 
                    type="danger" 
                    size="small" 
                    @click="confirmDeleteBranch(scope.row)"
                    :disabled="loadingBranches"
                  >
                    Delete
                  </el-button>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>

      <!-- Add/Edit Branch Dialog -->
      <el-dialog
        :title="editingBranch ? 'Edit Branch' : 'Add New Branch'"
        v-model="showAddBranchDialog"
        width="500px"
      >
        <el-form :model="newBranch" label-width="100px">
          <el-form-item label="Branch Name">
            <el-input 
              v-model="newBranch.branch_name" 
              placeholder="Enter branch name"
              :disabled="loadingBranches"
            />
          </el-form-item>
          <el-form-item label="Subsidiary">
            <el-select 
              v-model="newBranch.subsidiary_id" 
              placeholder="Select subsidiary"
              :disabled="loadingBranches"
              style="width: 100%"
            >
              <el-option 
                v-for="sub in subsidiaries" 
                :key="sub.id" 
                :label="sub.name" 
                :value="sub.id" 
              />
            </el-select>
          </el-form-item>
        </el-form>
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="showAddBranchDialog = false">Cancel</el-button>
            <el-button 
              type="primary" 
              @click="saveBranch"
              :disabled="!newBranch.branch_name || !newBranch.subsidiary_id || loadingBranches"
            >
              {{ editingBranch ? 'Save Changes' : 'Add Branch' }}
            </el-button>
          </span>
        </template>
      </el-dialog>

      <!-- Delete Branch Confirmation Dialog -->
      <el-dialog
        title="Confirm Delete"
        v-model="showDeleteBranchDialog"
        width="400px"
      >
        <p>Are you sure you want to delete this branch?</p>
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="showDeleteBranchDialog = false">Cancel</el-button>
            <el-button 
              type="danger" 
              @click="deleteBranch"
              :disabled="loadingBranches"
            >
              Delete
            </el-button>
          </span>
        </template>
      </el-dialog>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import axios from 'axios'

const router = useRouter()
const subsidiaries = ref([])
const loading = ref(false)

// Add new refs for expense types
const expenseTypes = ref([])
const loadingExpenseTypes = ref(false)
const showAddTypeDialog = ref(false)
const showDeleteDialog = ref(false)
const editingType = ref(null)
const typeToDelete = ref(null)

// Add ref for expense type subsidiaries
const expenseTypeSubsidiaries = ref([])

// Update newType ref to include id for editing
const newType = ref({
  id: null,
  type: '',
  category: '',
  subsidiary_id: null,
  selectedTypes: []
})

// Add new ref for existing types and assignments
const existingTypes = ref([])
const loadingExistingTypes = ref(false)
const typeAssignments = ref([])

// Add new refs for basis change dialog
const showBasisChangeDialog = ref(false)
const oldBasis = ref('')
const newBasis = ref('')
const selectedSubsidiary = ref(null)

// Add new refs for branch management
const branches = ref([])
const loadingBranches = ref(false)
const showAddBranchDialog = ref(false)
const showDeleteBranchDialog = ref(false)
const editingBranch = ref(null)
const branchToDelete = ref(null)

// Add new ref for branch form
const newBranch = ref({
  id: null,
  branch_name: '',
  subsidiary_id: ''
})

// Add new ref for type selection
const typeSelection = ref('existing')

// Create axios instance with default config
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add auth token to requests
api.interceptors.request.use(config => {
  const token = sessionStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

const fetchSubsidiaries = async () => {
  loading.value = true
  try {
    const response = await api.get('/api/admin/subsidiaries')
    subsidiaries.value = response.data
    // Fetch expense type subsidiaries
    const expenseTypeResponse = await api.get('/api/admin/expense-type-subsidiaries')
    expenseTypeSubsidiaries.value = expenseTypeResponse.data
  } catch (error) {
    ElMessage.error('Failed to fetch subsidiaries')
  } finally {
    loading.value = false
  }
}

const updateReportingBasis = (subsidiary) => {
  selectedSubsidiary.value = subsidiary
  oldBasis.value = subsidiary.reporting_basis === 'daily' ? 'Daily' : 'Weekly'
  newBasis.value = subsidiary.reporting_basis === 'daily' ? 'Weekly' : 'Daily'
  showBasisChangeDialog.value = true
}

const confirmBasisChange = async () => {
  if (!selectedSubsidiary.value) return
  
  loading.value = true
  try {
    const response = await api.put(`/api/admin/subsidiaries/${selectedSubsidiary.value.id}`, {
      reporting_basis: selectedSubsidiary.value.reporting_basis
    })
    ElMessage.success('Reporting basis updated successfully')
    showBasisChangeDialog.value = false
  } catch (error) {
    ElMessage.error('Failed to update reporting basis')
    // Revert the change in the UI
    await fetchSubsidiaries()
  } finally {
    loading.value = false
  }
}

const fetchExpenseTypes = async () => {
  loadingExpenseTypes.value = true
  try {
    // Get the current user's subsidiary ID
    const userData = JSON.parse(sessionStorage.getItem('user'))
    const subsidiaryId = userData.subsidiary_id

    // Fetch expense type subsidiaries first
    const [expenseTypesResponse, expenseTypeSubsidiariesResponse] = await Promise.all([
      api.get('/api/admin/expense-types'),
      api.get('/api/admin/expense-type-subsidiaries')
    ])

    // Filter expense types to only show those linked to the current subsidiary
    const linkedTypeIds = expenseTypeSubsidiariesResponse.data
      .filter(link => link.subsidiary_id === subsidiaryId)
      .map(link => link.expense_type_id)

    expenseTypes.value = expenseTypesResponse.data
      .filter(type => linkedTypeIds.includes(type.id))
  } catch (error) {
    ElMessage.error('Failed to fetch expense types')
  } finally {
    loadingExpenseTypes.value = false
  }
}

const fetchExistingTypes = async () => {
  loadingExistingTypes.value = true
  try {
    // Fetch all expense types
    const typesResponse = await api.get('/api/admin/expense-types')
    existingTypes.value = typesResponse.data

    // Fetch all assignments
    const assignmentsResponse = await api.get('/api/admin/expense-type-subsidiaries')
    typeAssignments.value = assignmentsResponse.data

  } catch (error) {
    if (error.response) {
    }
    ElMessage.error('Failed to fetch existing types')
  } finally {
    loadingExistingTypes.value = false
  }
}

const addExpenseType = async () => {
  loadingExpenseTypes.value = true
  try {
    await api.post('/api/admin/expense-types', newType.value)
    ElMessage.success('Expense type added successfully')
    newType.value = { type: '', category: '' }
    await fetchExpenseTypes()
  } catch (error) {
    ElMessage.error('Failed to add expense type')
  } finally {
    loadingExpenseTypes.value = false
  }
}

const removeExpenseType = async (type) => {
  loadingExpenseTypes.value = true
  try {
    await api.delete(`/api/admin/expense-types/${type.id}`)
    ElMessage.success('Expense type removed successfully')
    await fetchExpenseTypes()
  } catch (error) {
    ElMessage.error('Failed to remove expense type')
  } finally {
    loadingExpenseTypes.value = false
  }
}

// Add userData ref
const userData = ref(JSON.parse(sessionStorage.getItem('user')))

// Add new methods for managing types
const editType = (type) => {
  editingType.value = type
  newType.value = { ...type }
  showAddTypeDialog.value = true
}

const confirmDelete = (type) => {
  typeToDelete.value = type
  showDeleteDialog.value = true
}

const saveType = async () => {
  loadingExpenseTypes.value = true
  try {
    if (editingType.value) {
      // Update existing type
      await api.put(`/api/admin/expense-types/${editingType.value.id}`, {
        type: newType.value.type,
        category: newType.value.category
      })
      ElMessage.success('Type updated successfully')
    } else if (typeSelection.value === 'existing') {
      // Ensure selectedTypes is an array
      if (!Array.isArray(newType.value.selectedTypes)) {
        throw new Error('Invalid type selection')
      }
      
      // Assign multiple existing types to subsidiary
      const assignments = newType.value.selectedTypes.map(typeId => 
        api.post('/api/admin/expense-type-subsidiaries', {
          expense_type_id: typeId,
          subsidiary_id: newType.value.subsidiary_id
        })
      )
      await Promise.all(assignments)
      ElMessage.success('Types assigned successfully')
    } else {
      // Create new type
      const response = await api.post('/api/admin/expense-types', {
        type: newType.value.type,
        category: newType.value.category
      })
      const typeId = response.data.id

      // Create associations for new type
      const createAssociations = subsidiaries.value.map(subsidiary => 
        api.post('/api/admin/expense-type-subsidiaries', {
          expense_type_id: typeId,
          subsidiary_id: subsidiary.id
        })
      )
      await Promise.all(createAssociations)
      ElMessage.success('Type added successfully')
    }

    showAddTypeDialog.value = false
    resetForm()
    await fetchExpenseTypes()
    await fetchSubsidiaries()
  } catch (error) {
    ElMessage.error('Failed to save type')
  } finally {
    loadingExpenseTypes.value = false
  }
}

const deleteType = async () => {
  if (!typeToDelete.value) return

  loadingExpenseTypes.value = true
  try {
    // Get the current user's subsidiary ID
    const userData = JSON.parse(sessionStorage.getItem('user'))
    const subsidiaryId = userData.subsidiary_id

    // Delete the expense type-subsidiary association
    await api.delete(`/api/admin/expense-types/${typeToDelete.value.id}?subsidiary_id=${subsidiaryId}`)
    ElMessage.success('Type removed from subsidiary successfully')
    showDeleteDialog.value = false
    typeToDelete.value = null
    await fetchExpenseTypes()
    await fetchSubsidiaries() // Refresh subsidiaries to get updated associations
  } catch (error) {
    const errorData = error.response?.data
    let errorMessage = 'Failed to remove type'

    if (errorData) {
      if (errorData.error === 'Association not found') {
        errorMessage = 'This expense type is not associated with your subsidiary'
      } else if (errorData.error === 'Cannot remove expense type') {
        errorMessage = `Cannot remove type: ${errorData.message}`
      } else {
        errorMessage = errorData.message || errorData.error || 'Failed to remove type'
      }
    }

    ElMessage.error(errorMessage)
  } finally {
    loadingExpenseTypes.value = false
  }
}

const resetForm = () => {
  newType.value = {
    id: null,
    type: '',
    category: '',
    subsidiary_id: null,
    selectedTypes: []
  }
  editingType.value = null
  typeSelection.value = 'existing'
}

const isMobile = ref(window.innerWidth <= 768)

const handleResize = () => {
  isMobile.value = window.innerWidth <= 768
}

// Add function to get subsidiary name
const getSubsidiaryName = (subsidiaryId) => {
  const subsidiary = subsidiaries.value.find(sub => sub.id === subsidiaryId)
  return subsidiary ? subsidiary.name : 'Unknown'
}

// Add function to fetch branches
const fetchBranches = async () => {
  loadingBranches.value = true
  try {
    // Get all subsidiaries first
    const subsidiariesResponse = await api.get('/api/admin/subsidiaries')
    subsidiaries.value = subsidiariesResponse.data

    // Then fetch branches for each subsidiary
    const branchesPromises = subsidiaries.value.map(subsidiary => 
      api.get(`/api/branches?subsidiary_id=${subsidiary.id}`)
    )
    
    const branchesResponses = await Promise.all(branchesPromises)
    branches.value = branchesResponses.flatMap(response => response.data)
  } catch (error) {
    ElMessage.error('Failed to fetch branches')
  } finally {
    loadingBranches.value = false
  }
}

// Add function to edit branch
const editBranch = (branch) => {
  editingBranch.value = branch
  newBranch.value = { ...branch }
  showAddBranchDialog.value = true
}

// Add function to confirm branch deletion
const confirmDeleteBranch = (branch) => {
  branchToDelete.value = branch
  showDeleteBranchDialog.value = true
}

// Add function to save branch
const saveBranch = async () => {
  if (!newBranch.value.subsidiary_id) {
    ElMessage.error('Please select a subsidiary')
    return
  }

  loadingBranches.value = true
  try {
    if (editingBranch.value) {
      await api.put(`/api/branches/${editingBranch.value.id}`, {
        branch_name: newBranch.value.branch_name,
        subsidiary_id: newBranch.value.subsidiary_id
      })
      ElMessage.success('Branch updated successfully')
    } else {
      await api.post('/api/branches', {
        branch_name: newBranch.value.branch_name,
        subsidiary_id: newBranch.value.subsidiary_id
      })
      ElMessage.success('Branch added successfully')
    }
    showAddBranchDialog.value = false
    resetBranchForm()
    await fetchBranches()
  } catch (error) {
    ElMessage.error('Failed to save branch')
  } finally {
    loadingBranches.value = false
  }
}

// Add function to delete branch
const deleteBranch = async () => {
  if (!branchToDelete.value) return

  loadingBranches.value = true
  try {
    await api.delete(`/api/branches/${branchToDelete.value.id}`)
    ElMessage.success('Branch deleted successfully')
    showDeleteBranchDialog.value = false
    branchToDelete.value = null
    await fetchBranches()
  } catch (error) {
    ElMessage.error('Failed to delete branch')
  } finally {
    loadingBranches.value = false
  }
}

// Add function to reset branch form
const resetBranchForm = () => {
  newBranch.value = {
    id: null,
    branch_name: '',
    subsidiary_id: ''
  }
  editingBranch.value = null
}

// Add computed property for form validation
const isFormValid = computed(() => {
  if (editingType.value) {
    return newType.value.type && newType.value.category
  }
  if (typeSelection.value === 'existing') {
    return Array.isArray(newType.value.selectedTypes) && 
           newType.value.selectedTypes.length > 0 && 
           newType.value.subsidiary_id
  }
  return newType.value.type && newType.value.category && newType.value.subsidiary_id
})

// Add computed property for submit button text
const getSubmitButtonText = computed(() => {
  if (editingType.value) return 'Save Changes'
  if (typeSelection.value === 'existing') return 'Assign Types'
  return 'Add Type'
})

// Add function to handle type selection
const handleTypeSelect = (value) => {
  if (!Array.isArray(value)) {
    newType.value.selectedTypes = []
    return
  }
  newType.value.selectedTypes = value
}

// Add function to handle subsidiary selection
const handleSubsidiarySelect = (value) => {
  // Reset type selection when subsidiary changes
  newType.value.selectedTypes = []
}

// Add computed property for available types
const availableTypes = computed(() => {

  if (!newType.value.subsidiary_id) {
    return []
  }

  // Get IDs of types already assigned to the selected subsidiary
  const assignedTypeIds = typeAssignments.value
    .filter(assignment => assignment.subsidiary_id === newType.value.subsidiary_id)
    .map(assignment => assignment.expense_type_id)



  // Filter out types that are already assigned to this subsidiary
  const available = existingTypes.value.filter(type => !assignedTypeIds.includes(type.id))


  return available
})

// Add new method to handle opening the add dialog
const openAddTypeDialog = () => {
  editingType.value = null
  typeSelection.value = 'existing'
  newType.value = {
    id: null,
    type: '',
    category: '',
    subsidiary_id: subsidiaries.value.length > 0 ? subsidiaries.value[0].id : null,
    selectedTypes: []
  }
  showAddTypeDialog.value = true
}

onMounted(async () => {
  window.addEventListener('resize', handleResize)
  // Check if user is admin
  const userData = sessionStorage.getItem('user')
  if (userData) {
    const user = JSON.parse(userData)
    if (user.role.toLowerCase() !== 'admin') {
      router.push('/')
    } else {
      try {
        await fetchSubsidiaries()
        await fetchExpenseTypes()
        await fetchExistingTypes()
        await fetchBranches()
      } catch (error) {
        ElMessage.error('Failed to load initial data')
      }
    }
  } else {
    router.push('/')
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
.subsidiary-management {
  padding: 24px;
  background: #f8fafc;
  min-height: 100vh;
}

.dashboard-header {
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.header-content h1 {
  font-size: 1.875rem;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.025em;
}

.subtitle {
  font-size: 0.875rem;
  color: #64748b;
}

.registration-container {
  width: 100%;
}

.card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
  margin-bottom: 24px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
  gap: 8px;
}

.card-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.card-content {
  padding: 24px;
}

:deep(.el-table) {
  --el-table-border-color: #e2e8f0;
  --el-table-header-bg-color: #f1f5f9;
  border-radius: 8px;
  overflow: hidden;
}

:deep(.el-table__header) {
  border-bottom: 2px solid #e2e8f0;
}

:deep(.el-table__row) {
  transition: all 0.2s ease;
}

:deep(.el-table__row:hover) {
  background-color: #f8fafc;
}

:deep(.el-table__cell) {
  border-bottom: 1px solid #e2e8f0;
}

.subsidiary-name {
  display: flex;
  align-items: center;
  gap: 12px;
}

.subsidiary-name .name {
  font-weight: 500;
}

.basis-cell {
  display: flex;
  align-items: center;
}

.basis-select {
  width: 100%;
}

:deep(.el-select .el-input__wrapper) {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  box-shadow: none;
}

:deep(.el-select .el-input__wrapper:hover) {
  border-color: #94a3b8;
}

:deep(.el-select .el-input__wrapper.is-focus) {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

:deep(.el-table__empty-block) {
  background: #f8fafc;
  min-height: 200px;
}

:deep(.el-table__empty-text) {
  color: #64748b;
  font-size: 0.875rem;
}

.mt-8 {
  margin-top: 2rem;
}

.mt-6 {
  margin-top: 1.5rem;
}

.text-lg {
  font-size: 1.125rem;
}

.font-semibold {
  font-weight: 600;
}

.mb-4 {
  margin-bottom: 1rem;
}

.flex {
  display: flex;
}

.gap-4 {
  gap: 1rem;
}

:deep(.el-tag) {
  text-transform: capitalize;
}

:deep(.el-input) {
  flex: 2;
}

:deep(.el-select) {
  flex: 1;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

:deep(.el-dialog__body) {
  padding: 20px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.subsidiary-mobile-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  border: 1px solid #e2e8f0;
  margin-bottom: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.mobile-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}
.mobile-label {
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 500;
}
.mobile-value {
  font-size: 1rem;
  color: #1e293b;
  font-weight: 600;
}

.expense-mobile-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  border: 1px solid #e2e8f0;
  margin-bottom: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.action-row {
  justify-content: flex-end;
  gap: 8px;
}

@media (max-width: 768px) {
  .subsidiary-management {
    padding: 8px;
  }
  .dashboard-header {
    margin-bottom: 16px;
  }
  .header-content h1 {
    font-size: 1.25rem;
  }
  .subtitle {
    font-size: 0.75rem;
  }
  .card {
    border-radius: 8px;
    margin-bottom: 16px;
  }
  .card-header {
    padding: 12px;
    font-size: 1rem;
  }
  .card-header h2 {
    font-size: 1rem;
  }
  .card-content {
    padding: 12px;
  }
  :deep(.el-table__cell) {
    padding: 8px 6px !important;
    font-size: 0.75rem !important;
  }
  :deep(.el-table__header th) {
    padding: 8px 6px !important;
    font-size: 0.75rem !important;
  }
  :deep(.el-dialog) {
    width: 95vw !important;
    min-width: unset !important;
    max-width: 100vw !important;
  }
  :deep(.el-dialog__body) {
    padding: 12px !important;
  }
  .action-buttons {
    gap: 4px;
  }
}

@media (max-width: 480px) {
  .subsidiary-management {
    padding: 4px;
  }
  .card {
    border-radius: 6px;
    margin-bottom: 8px;
  }
  .card-header {
    padding: 8px;
    font-size: 0.95rem;
  }
  .card-header h2 {
    font-size: 0.95rem;
  }
  .card-content {
    padding: 6px;
  }
  :deep(.el-table__cell) {
    padding: 4px 2px !important;
    font-size: 0.7rem !important;
  }
  :deep(.el-table__header th) {
    padding: 4px 2px !important;
    font-size: 0.7rem !important;
  }
  :deep(.el-dialog) {
    width: 99vw !important;
    min-width: unset !important;
    max-width: 100vw !important;
  }
  :deep(.el-dialog__body) {
    padding: 6px !important;
  }
}

.basis-change-warning {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
  padding: 16px;
}

.basis-change-warning i {
  font-size: 2rem;
  color: #f59e0b;
}

.basis-change-warning p {
  margin: 0;
  color: #1e293b;
  font-size: 0.875rem;
  line-height: 1.5;
}

.basis-change-warning strong {
  color: #0ea5e9;
  font-weight: 600;
}

.branch-mobile-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  border: 1px solid #e2e8f0;
  margin-bottom: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.branch-name {
  display: flex;
  align-items: center;
  gap: 12px;
}

.branch-name .name {
  font-weight: 500;
}

.type-selection-group {
  display: flex;
  gap: 24px;
}

:deep(.el-radio) {
  margin-right: 0;
}
</style> 